import { memo, useMemo } from 'react';
import styled from 'styled-components';

import { formatTimecode } from '../../../utils/timecode';

/**
 * タイムラインの時間ルーラー。
 * ズーム倍率（pixelsPerSecond）に応じて目盛り間隔を適応的に選択し、
 * 可視範囲 [scrollLeft, scrollLeft + viewportWidth] の目盛りのみを描画する。
 * フレームレベルまでズームすると M:SS.FF 表記に切り替わる。
 */

export interface TimelineRulerProps {
  duration: number;
  fps: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  viewportWidth: number;
  height: number;
}

const RulerLane = styled.div<{ $height: number }>`
  position: relative;
  height: ${p => p.$height}px;
  background: #fff;
  border-bottom: 1px solid var(--soremo-border);
  cursor: ew-resize;
  /* 最終目盛りのラベル（nowrap）がコンテンツ幅の外へはみ出して
     スクロール可能幅を広げないよう遮断する */
  overflow: hidden;
`;

const MajorTick = styled.div`
  position: absolute;
  bottom: 0;
  width: 1px;
  height: 10px;
  background: var(--soremo-light-gray);
  pointer-events: none;
`;

const MinorTick = styled.div`
  position: absolute;
  bottom: 0;
  width: 1px;
  height: 5px;
  background: var(--soremo-border);
  pointer-events: none;
`;

const TickLabel = styled.div.attrs({ className: 'bodytext-11' })`
  position: absolute;
  top: 2px;
  transform: translateX(3px);
  line-height: 1;
  color: var(--soremo-light-gray);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
`;

/** ラベル付き目盛りの最小間隔（px） */
const MIN_LABEL_SPACING = 72;
/** 小目盛りの最小間隔（px） */
const MIN_MINOR_SPACING = 6;

/** ズーム倍率に応じた目盛りステップ（秒）を選択 */
function selectStep(pixelsPerSecond: number, fps: number): number {
  const frameDur = 1 / fps;
  const candidates = [
    frameDur,
    frameDur * 2,
    frameDur * 5,
    frameDur * 10,
    1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 1800, 3600,
  ];
  for (const step of candidates) {
    if (step * pixelsPerSecond >= MIN_LABEL_SPACING) return step;
  }
  return candidates[candidates.length - 1];
}

interface Tick {
  x: number;
  label: string | null;
}

const TimelineRuler = memo(function TimelineRuler({
  duration,
  fps,
  pixelsPerSecond,
  scrollLeft,
  viewportWidth,
  height,
}: TimelineRulerProps) {
  const ticks = useMemo((): Tick[] => {
    if (duration <= 0 || pixelsPerSecond <= 0 || viewportWidth <= 0) return [];

    const step = selectStep(pixelsPerSecond, fps);
    const withFrames = step < 1;

    // 小目盛り分割数（間隔が確保できる範囲で 5 → 2 → なし）
    let minorDiv = 0;
    if ((step / 5) * pixelsPerSecond >= MIN_MINOR_SPACING) minorDiv = 5;
    else if ((step / 2) * pixelsPerSecond >= MIN_MINOR_SPACING) minorDiv = 2;

    const tStart = Math.max(0, Math.floor(scrollLeft / pixelsPerSecond / step) * step);
    const tEnd = Math.min(duration, (scrollLeft + viewportWidth) / pixelsPerSecond + step);

    const result: Tick[] = [];
    // 浮動小数の蓄積誤差を避けるためインデックスから都度計算する
    const startIdx = Math.round(tStart / step);
    const endIdx = Math.ceil(tEnd / step);
    for (let i = startIdx; i <= endIdx; i++) {
      const t = i * step;
      if (t > duration + step / 2) break;
      result.push({
        x: t * pixelsPerSecond,
        label: formatTimecode(t, { fps, withFrames }),
      });
      if (minorDiv > 0) {
        for (let m = 1; m < minorDiv; m++) {
          const tm = t + (step * m) / minorDiv;
          if (tm > duration) break;
          result.push({ x: tm * pixelsPerSecond, label: null });
        }
      }
    }
    return result;
  }, [duration, fps, pixelsPerSecond, scrollLeft, viewportWidth]);

  return (
    <RulerLane $height={height}>
      {ticks.map(tick =>
        tick.label !== null ? (
          <div key={tick.x}>
            <MajorTick style={{ left: tick.x }} />
            <TickLabel style={{ left: tick.x }}>{tick.label}</TickLabel>
          </div>
        ) : (
          <MinorTick key={tick.x} style={{ left: tick.x }} />
        ),
      )}
    </RulerLane>
  );
});

export default TimelineRuler;
